// ── AttachmentsPanel ──────────────────────────────────────────────────────────
// Drop-in attachments block for ANY document: <AttachmentsPanel refType="PaymentVoucher" refId={v._id} />
// Lists the files on a document, downloads on click, uploads (unless
// canUpload={false}) and deletes (uploader or admin). Backed by the generic
// /api/attachments store — same component works for messages, purchase orders,
// invoices, leave applications, employee documents…
import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, IconButton, Button, Chip, CircularProgress, Tooltip,
} from '@mui/material';
import { AttachFile, Download, Delete, InsertDriveFile } from '@mui/icons-material';
import axios from 'axios';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => {
  // Token is stored as its own localStorage key at login (see Login.js), not on
  // the user object. Reading it off the user object returned an empty header,
  // triggering a 401 that logged parents out when opening a message's attachments.
  const u = JSON.parse(localStorage.getItem('user') || '{}');
  const token = localStorage.getItem('token') || u?.token || u?.authToken || '';
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const fmtSize = (b) => b > 1024 * 1024
  ? `${(b / 1024 / 1024).toFixed(1)} MB`
  : `${Math.max(1, Math.round(b / 1024))} KB`;

// Attach-on-send helper: upload a file against a document that was just created
export const uploadAttachment = async (file, refType, refId) => {
  const form = new FormData();
  form.append('file', file);
  form.append('refType', refType);
  form.append('refId', refId);
  const r = await axios.post(`${API}/api/attachments`, form, { headers: hdrs() });
  return r.data;
};

export const downloadAttachment = async (att) => {
  const r = await axios.get(`${API}/api/attachments/${att._id}/download`, {
    headers: hdrs(), responseType: 'blob',
  });
  const url = URL.createObjectURL(r.data);
  const a = document.createElement('a');
  a.href = url; a.download = att.fileName;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
};

export default function AttachmentsPanel({ refType, refId, canUpload = true, title = 'Attachments' }) {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    if (!refType || !refId) { setFiles([]); return; }
    try {
      const r = await axios.get(`${API}/api/attachments`, { params: { refType, refId }, headers: hdrs() });
      setFiles(r.data?.data || []);
    } catch { /* leave list as-is */ }
  }, [refType, refId]);

  useEffect(() => { load(); }, [load]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    setBusy(true); setError('');
    try {
      await uploadAttachment(file, refType, refId);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed');
    } finally { setBusy(false); }
  };

  const remove = async (att) => {
    try {
      await axios.delete(`${API}/api/attachments/${att._id}`, { headers: hdrs() });
      setFiles(fs => fs.filter(f => f._id !== att._id));
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed');
    }
  };

  if (!refId) return null;

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={files.length || canUpload ? 1 : 0}>
        <AttachFile sx={{ fontSize: 16, color: 'text.disabled' }} />
        <Typography variant="caption" color="text.secondary" fontWeight={700}>
          {title}{files.length ? ` (${files.length})` : ''}
        </Typography>
        {canUpload && (
          <>
            <Button size="small" onClick={() => inputRef.current?.click()} disabled={busy}
              startIcon={busy ? <CircularProgress size={12} /> : <AttachFile sx={{ fontSize: 14 }} />}
              sx={{ ml: 'auto', textTransform: 'none', fontSize: 12 }}>
              {busy ? 'Uploading…' : 'Attach file'}
            </Button>
            <input type="file" hidden ref={inputRef} onChange={onPick} />
          </>
        )}
      </Box>

      {error && <Typography variant="caption" color="error" display="block" mb={0.5}>{error}</Typography>}

      <Box display="flex" flexDirection="column" gap={0.5}>
        {files.map(att => {
          const canDelete = canUpload &&
            (att.uploadedBy === user.email || user.role === 'admin' || user.isSuperAdmin);
          return (
            <Box key={att._id} display="flex" alignItems="center" gap={1}
              sx={{ p: 0.8, px: 1.2, bgcolor: 'grey.50', borderRadius: 1.5 }}>
              <InsertDriveFile sx={{ fontSize: 18, color: 'primary.main' }} />
              <Box flex={1} minWidth={0}>
                <Typography variant="body2" fontWeight={600} noWrap>{att.fileName}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {fmtSize(att.size)}{att.uploadedBy ? ` • ${att.uploadedBy}` : ''}
                </Typography>
              </Box>
              <Tooltip title="Download">
                <IconButton size="small" color="primary" onClick={() => downloadAttachment(att)}>
                  <Download fontSize="small" />
                </IconButton>
              </Tooltip>
              {canDelete && (
                <Tooltip title="Remove">
                  <IconButton size="small" color="error" onClick={() => remove(att)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          );
        })}
      </Box>

      {files.length === 0 && !canUpload && (
        <Chip label="No attachments" size="small" variant="outlined" sx={{ fontSize: 11 }} />
      )}
    </Box>
  );
}

